/**
 * PTEL R2 Configuration
 * 
 * Configuración para acceso a Cloudflare R2 con fallback a datos locales.
 * Incluye Circuit Breaker para evitar cascadas de fallos.
 * 
 * @module lib/localData/r2Config
 * @version 2.0.0
 * @date 2026-01-28
 * @session Fase 4 - Circuit Breaker implementation
 */

import { WORKER_BASE_V1 } from '@/lib/config/workerEndpoints';

// ============================================================================
// CONFIGURACIÓN R2
// ============================================================================

/**
 * URL base del Worker R2 — re-export desde `workerEndpoints` (SoT canónica).
 * Migrado en DRIFT-H4XR-2: el dominio real es ahora `geodata.pteltool.es/v1`.
 * Se preserva el nombre `R2_BASE_URL` para compatibilidad con importadores externos.
 */
export { WORKER_BASE_V1 as R2_BASE_URL };

/**
 * Timeout para requests a R2 (ms)
 */
export const R2_TIMEOUT = 10000;

/**
 * Número de reintentos antes de fallback
 */
export const R2_RETRIES = 1;

/**
 * Mapeo de archivos locales a rutas R2
 */
export const R2_FILE_MAP: Record<string, string> = {
  // DERA
  'dera-dexie/all-dera.json': 'dera/all-dera.json.gz',
  'dera-dexie/health.json': 'dera/health.json.gz',
  'dera-dexie/education.json': 'dera/education.json.gz',
  'dera-dexie/security.json': 'dera/security.json.gz',
  'dera-dexie/municipal.json': 'dera/municipal.json.gz',
  'dera-dexie/energy.json': 'dera/energy.json.gz',
  'dera-dexie/emergency.json': 'dera/emergency.json.gz',
  
  // INE
  'ine/municipios.json': 'ine/municipios.json.gz',
};

/**
 * Obtiene la URL R2 para un archivo local
 */
export function getR2Url(localFile: string): string | null {
  const r2Path = R2_FILE_MAP[localFile];
  if (!r2Path) return null;
  return `${WORKER_BASE_V1}/${r2Path}`;
}

/**
 * Verifica si R2 está habilitado
 */
export function isR2Enabled(): boolean {
  // Puede deshabilitarse via variable de entorno o localStorage
  if (typeof localStorage !== 'undefined') {
    const disabled = localStorage.getItem('ptel_r2_disabled');
    if (disabled === 'true') return false;
  }
  return true;
}

/**
 * Estadísticas de uso R2 (para debugging)
 */
export interface R2Stats {
  hits: number;
  misses: number;
  errors: number;
  lastError?: string;
}

let r2Stats: R2Stats = { hits: 0, misses: 0, errors: 0 };

export function getR2Stats(): R2Stats {
  return { ...r2Stats };
}

export function recordR2Hit(): void {
  r2Stats.hits++;
}

export function recordR2Miss(): void {
  r2Stats.misses++;
}

export function recordR2Error(error: string): void {
  r2Stats.errors++;
  r2Stats.lastError = error;
}

export function resetR2Stats(): void {
  r2Stats = { hits: 0, misses: 0, errors: 0 };
}

// ============================================================================
// CIRCUIT BREAKER
// ============================================================================

/**
 * Estado del Circuit Breaker
 * - closed: Funcionamiento normal, intenta R2
 * - open: R2 deshabilitado temporalmente, usa fallback directo
 * - half-open: Probando si R2 se recuperó
 */
export type CircuitState = 'closed' | 'open' | 'half-open';

interface CircuitBreakerState {
  state: CircuitState;
  failures: number;
  lastFailure: number;
  lastSuccess: number;
  openedAt: number | null;
}

/** Configuración del Circuit Breaker */
const CIRCUIT_CONFIG = {
  /** Fallos consecutivos para abrir el circuito */
  failureThreshold: 3,
  /** Tiempo en ms antes de probar recuperación (30s) */
  resetTimeout: 30000,
  /** Tiempo en ms para considerar un fallo "reciente" (60s) */
  failureWindow: 60000,
};

let circuitBreaker: CircuitBreakerState = {
  state: 'closed',
  failures: 0,
  lastFailure: 0,
  lastSuccess: Date.now(),
  openedAt: null,
};

/**
 * Obtiene el estado actual del Circuit Breaker
 */
export function getCircuitState(): CircuitState {
  // Si está abierto, verificar si es hora de probar recuperación
  if (circuitBreaker.state === 'open') {
    const timeSinceOpen = Date.now() - (circuitBreaker.openedAt || 0);
    if (timeSinceOpen >= CIRCUIT_CONFIG.resetTimeout) {
      circuitBreaker.state = 'half-open';
      console.log('[R2 Circuit] Transitioning to half-open state, testing R2...');
    }
  }
  return circuitBreaker.state;
}

/**
 * Registra un fallo en R2 y actualiza el Circuit Breaker
 */
export function recordCircuitFailure(): void {
  const now = Date.now();
  
  // Si el último fallo fue hace mucho, resetear contador
  if (now - circuitBreaker.lastFailure > CIRCUIT_CONFIG.failureWindow) {
    circuitBreaker.failures = 0;
  }
  
  circuitBreaker.failures++;
  circuitBreaker.lastFailure = now;
  
  // Si alcanzamos el umbral, abrir el circuito
  if (circuitBreaker.failures >= CIRCUIT_CONFIG.failureThreshold && circuitBreaker.state !== 'open') {
    circuitBreaker.state = 'open';
    circuitBreaker.openedAt = now;
    console.warn(`[R2 Circuit] OPENED after ${circuitBreaker.failures} failures. Fallback to local for ${CIRCUIT_CONFIG.resetTimeout/1000}s`);
  }
  
  // Si estábamos en half-open y falló, volver a abrir
  if (circuitBreaker.state === 'half-open') {
    circuitBreaker.state = 'open';
    circuitBreaker.openedAt = now;
    console.warn('[R2 Circuit] Half-open test FAILED, reopening circuit');
  }
}

/**
 * Registra un éxito en R2 y cierra el Circuit Breaker
 */
export function recordCircuitSuccess(): void {
  const wasOpen = circuitBreaker.state !== 'closed';
  
  circuitBreaker.state = 'closed';
  circuitBreaker.failures = 0;
  circuitBreaker.lastSuccess = Date.now();
  circuitBreaker.openedAt = null;
  
  if (wasOpen) {
    console.log('[R2 Circuit] CLOSED - R2 recovered successfully');
  }
}

/**
 * Verifica si debemos intentar R2 según el Circuit Breaker
 */
export function shouldTryR2(): boolean {
  const state = getCircuitState();
  
  // En estado cerrado, siempre intentar
  if (state === 'closed') return true;
  
  // En half-open, permitir un intento de prueba
  if (state === 'half-open') return true;
  
  // En estado abierto, no intentar
  return false;
}

/**
 * Resetea el Circuit Breaker (para testing/debugging)
 */
export function resetCircuitBreaker(): void {
  circuitBreaker = {
    state: 'closed',
    failures: 0,
    lastFailure: 0,
    lastSuccess: Date.now(),
    openedAt: null,
  };
  console.log('[R2 Circuit] Reset to closed state');
}

/**
 * Obtiene estadísticas completas incluyendo Circuit Breaker
 */
export function getR2FullStats(): R2Stats & { circuit: CircuitBreakerState } {
  return {
    ...getR2Stats(),
    circuit: { ...circuitBreaker }
  };
}
